import React, { useMemo, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { Check } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';
import { isArray } from 'lodash';

import { UnidataTable, Modal } from '~/components';
import { t } from '~/helpers';
import api from '~/services/api';
import {
  deleteArqueacaoAuto,
  findArqueacaoAuto,
  recordFiltering,
} from '~/store/modules/arqueacaoAutomatica/actions';

import DialogDadosTabela from './DialogDadosTabela';

function ArqueacaoAutomatica() {
  const redux = useSelector((state) => state.arqueacaoautomatica);
  const dispatch = useDispatch();

  const [row, setRow] = useState(null);

  const arqueacaoAutoRef = useRef(null);
  const confirmCancelRef = useRef(null);
  const confirmUpdateRef = useRef(null);
  const confirmEndRef = useRef(null);

  const [canUpdate, setCanUpdate] = useState(true);

  const openDialogDadosTabela = useCallback((data) => {
    console.log(data);
    if (
      !data ||
      !data.alturamaximaarqueacao ||
      !data.alturamaximaarqueacaoautomatica ||
      !data.alturaminimaarqueacaoautomatica
    ) {
      return;
    }

    const {
      alturamaximaarqueacao,
      alturamaximaarqueacaoautomatica,
      alturaminimaarqueacaoautomatica,
    } = data;

    if (
      alturaminimaarqueacaoautomatica > 0 &&
      alturamaximaarqueacaoautomatica > alturaminimaarqueacaoautomatica
    ) {
      setCanUpdate(true);
    } else {
      setCanUpdate(false);
    }

    arqueacaoAutoRef.current.handleOpenModal({ data });
  });

  const button = useMemo(() => [
    {
      title: t('s.geral.botaoalterar'),
      icon: <Check />,
      action: openDialogDadosTabela,
      color: 'primary',
    },
  ]);

  const actions = useMemo(
    () => [
      {
        label: t('s.geral.aprovar'),
        callback: (data) => confirmUpdateRef.current.handleOpenModal({ data }),
        color: 'primary',
        autoFocus: false,
        disabled: !canUpdate,
        variant: 'contained',
      },
      {
        label: t('Finalizar'),
        callback: (data) => confirmEndRef.current.handleOpenModal({ data }),
        color: 'secondary',
        autoFocus: false,
        disabled: false,
        variant: 'contained',
      },
      {
        label: t('s.geral.botaovoltar'),
        callback: () => arqueacaoAutoRef.current.handleCloseModal(),
        color: 'default',
        autoFocus: false,
        disabled: false,
        variant: 'contained',
      },
    ],
    [row, canUpdate]
  );

  const header = useMemo(
    () => [
      {
        id: 'datahorainiciohistoricoarqaut',
        label: t('s.operacao.abastecimento.datahorainicialmovimentacao'),
        filter: {
          type: 'initialdatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 00:00:00'),
        },
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
      },
      {
        id: 'codigoposto',
        label: t('s.configuracao.arqueacao.idposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigotanquefisico',
        label: t('s.operacao.turno.tanque'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeproduto',
        label: t('s.menu.configuracao.produto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'idterminal',
        label: t('s.configuracao.terminal.idterminal'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigousuario',
        label: t('s.relatorio.reltransferencia.codigousuario'),
        filter: {
          type: 'text',
        },
      },
    ],
    []
  );

  const handleUpdateArqueacao = useCallback(
    async (id) => {
      try {
        await api.put(`arqueacaoautomatica/atualiza/${id}`);
        toast.success(t('s.operacao.arqueacaoautomatica.atualizada'));
        confirmationCloseModals();
        dispatch(recordFiltering(redux.params));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [redux.params]
  );

  const handleCancelArqueacao = async (idPosto, idTanqueFisico) => {
    try {
      await api.put(`arqueacaotanque/cancela/${idPosto}/${idTanqueFisico}`);
      toast.success(t('s.operacao.arqueacaoautomatica.cancelada'));
      confirmationCloseModals();
      dispatch(recordFiltering(redux.params));
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  };

  const handleFinalizarArqueacao = async (idPosto, idTanqueFisico) => {
    try {
      await api.put(`arqueacaotanque/finaliza/${idPosto}/${idTanqueFisico}`);
      toast.success(t('Arqueação finalizada'));
      confirmationCloseModals();
      dispatch(recordFiltering(redux.params));
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  };

  const confirmationCloseModals = () => {
    confirmCancelRef.current.handleCloseModal();
    confirmUpdateRef.current.handleCloseModal();
    arqueacaoAutoRef.current.handleCloseModal();
    confirmEndRef.current.handleCloseModal();
  };

  return (
    <>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        delete={false}
        update={false}
        create={false}
        read={recordFiltering}
        find={findArqueacaoAuto}
        remove={deleteArqueacaoAuto}
        keys="idarqueacaoautomatica"
        actions={button}
        onRow={(value) => setRow(value)}
      />
      <Modal
        ref={arqueacaoAutoRef}
        component={DialogDadosTabela}
        width="xs"
        labelCancell={t('s.geral.botaovoltar')}
        title={t('s.operacao.arqueacaoautomatica.atualizacao')}
        onForm={() => dispatch(recordFiltering(redux.params))}
        actions={actions}
      />

      <Modal
        ref={confirmCancelRef}
        component={() => <p>{t('s.geral.acaonaopoderaserdesfeita')}</p>}
        title={t('s.geral.reprovaarqueacaoautomatica')}
        maxWidth="xs"
        loading={redux.loading}
        actions={[
          {
            label: t('s.geral.reprovar'),
            autoFocus: true,
            variant: 'outlined',
            color: 'primary',
            callback: (data) =>
              handleCancelArqueacao(data.idposto, data.idtanquefisico),
          },
          {
            label: t('s.geral.botaovoltar'),
            variant: 'outlined',
            color: 'secondary',
            callback: () => confirmCancelRef.current.handleCloseModal(),
          },
        ]}
      />
      <Modal
        ref={confirmEndRef}
        component={() => <p>{t('s.geral.acaonaopoderaserdesfeita')}</p>}
        title={t('Finalizar Arqueação Automática')}
        maxWidth="xs"
        loading={redux.loading}
        actions={[
          {
            label: t('Finalizar Arqueação'),
            autoFocus: true,
            variant: 'outlined',
            color: 'primary',
            callback: (data) =>
              handleFinalizarArqueacao(data.idposto, data.idtanquefisico),
          },
          {
            label: t('s.geral.botaovoltar'),
            callback: () => confirmEndRef.current.handleCloseModal(),
            variant: 'outlined',
            color: 'secondary',
          },
        ]}
      />
      <Modal
        ref={confirmUpdateRef}
        component={() => <p>{t('s.geral.acaonaopoderaserdesfeita')}</p>}
        title={t('s.geral.aprovararqueacaoautomatica')}
        maxWidth="xs"
        loading={redux.loading}
        actions={[
          {
            label: t('s.geral.aprovar'),
            autoFocus: true,
            variant: 'outlined',
            color: 'primary',
            callback: (data) =>
              handleUpdateArqueacao(data.idarqueacaoautomatica),
          },
          {
            label: t('s.geral.botaovoltar'),
            callback: () => confirmUpdateRef.current.handleCloseModal(),
            variant: 'outlined',
            color: 'secondary',
          },
        ]}
      />
    </>
  );
}

export default ArqueacaoAutomatica;
